import React from 'react';
import { Switch as AriaSwitch, SwitchProps as AriaSwitchProps } from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { composeTailwindRenderProps, focusRing } from './utils';

export interface SwitchProps extends Omit<AriaSwitchProps, 'children'> {
  children: React.ReactNode;
  alignIndicator?: 'start' | 'end';
}

const track = tv({
  extend: focusRing,
  base: 'flex h-4 w-7 px-px items-center shrink-0 cursor-default rounded-full transition duration-200 ease-in-out shadow-inner border border-transparent',
  variants: {
    isSelected: {
      false:
        'bg-gray-500 group-pressed:bg-gray-600 dark:bg-gray-500 dark:group-pressed:bg-gray-600',
      true: 'bg-purple-700 forced-colors:bg-[Highlight]! group-pressed:bg-purple-800',
    },
    isDisabled: {
      true: 'bg-gray-200 dark:bg-gray-700 forced-colors:group-selected:bg-[GrayText]! forced-colors:border-[GrayText]',
    },
  },
});

const handle = tv({
  base: 'h-3 w-3 transform rounded-full bg-white dark:bg-gray-100 outline outline-1 -outline-offset-1 outline-transparent shadow-xs transition duration-200 ease-in-out',
  variants: {
    isSelected: {
      false: 'translate-x-0',
      true: 'translate-x-full',
    },
    isDisabled: {
      true: 'forced-colors:outline-[GrayText]',
    },
  },
});

export function ClarionSwitch({ children, alignIndicator = 'start', ...props }: SwitchProps) {
  return (
    <AriaSwitch
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        'cursor-pointer group flex gap-2 items-center text-gray-800 disabled:text-gray-300 dark:text-gray-200 dark:disabled:text-gray-600 forced-colors:disabled:text-[GrayText] text-sm transition',
      )}
    >
      {(renderProps) => (
        <>
          {alignIndicator === 'end' && children}
          <div className={track(renderProps)}>
            <span className={handle(renderProps)} />
          </div>
          {alignIndicator === 'start' && children}
        </>
      )}
    </AriaSwitch>
  );
}
